import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface InfoTooltipProps {
  title: string;
  items: string[];
  'data-info-spot'?: string;
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({ title, items, ...rest }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculate position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.right + window.scrollX - 288 // 288 = w-72 (width of tooltip)
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Backdrop
  const backdrop = isOpen ? (
    <div 
      className="fixed inset-0 bg-black/10 z-[9997]"
      onClick={() => setIsOpen(false)}
    />
  ) : null;

  // Tooltip content via Portal
  const tooltipContent = isOpen ? (
    <div 
      className="fixed z-[9998] w-72 p-4 bg-white rounded-xl shadow-2xl border border-gray-200 text-left"
      style={{ 
        top: `${position.top}px`, 
        left: `${position.left}px`,
        maxHeight: '80vh',
        overflowY: 'auto'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header with close button */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(false);
          }}
          className="text-gray-400 hover:text-gray-600 text-lg leading-none"
        >
          ×
        </button>
      </div>
      
      <ul className="space-y-1.5">
        {items.map((item, index) => (
          <li key={index} className="flex items-start gap-2 text-xs text-gray-600">
            <span className="text-[#7A9488] mt-0.5">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  return (
    <>
      <div className="relative inline-block">
        <button
          ref={buttonRef}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          data-info-spot={rest['data-info-spot']}
          className={`homepage-info-button w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors`}
          title="Meer informatie"
          aria-label="Meer informatie"
        >
          <span className="text-xs font-semibold">i</span>
        </button>
      </div>
      
      {createPortal(
        <>
          {backdrop}
          {tooltipContent}
        </>,
        document.body
      )}
    </>
  );
};

export default InfoTooltip;
